import React from "react";
import axios from "axios";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Copy, Image as ImageIcon, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { copyToClipboard } from "../../utils/clipboard";
import RegenerateSection from "./RegenerateSection";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function InfographicTab({ content, onRegenerate, isRegenerating, onRegenerateItem, regeneratingItem }) {
  const [sectionImages, setSectionImages] = React.useState({});
  const [generatingImage, setGeneratingImage] = React.useState(null);

  const generateImage = async (section, index) => {
    setGeneratingImage(index);
    try {
      const prompt = `Create an infographic section visual for: ${section.title}. Visual direction: ${section.visual}`;
      
      const response = await axios.post(`${API}/images/generate`, {
        prompt,
        content_type: "infographic"
      });

      setSectionImages(prev => ({
        ...prev,
        [index]: response.data.image_url
      }));

      toast.success("Image generated!");
    } catch (error) {
      console.error("Image generation error:", error);
      toast.error("Failed to generate image");
    } finally {
      setGeneratingImage(null);
    }
  };

  const copyAll = () => {
    const allText = content.map((section, i) => 
      `${i + 1}. ${section.title}\n${section.content}\nVisual: ${section.visual}`
    ).join("\n\n");
    copyToClipboard(allText);
  };

  if (!content || content.length === 0) {
    return (
      <div className="text-center text-white/60 py-12">
        <p className="mb-4">No infographic content generated yet.</p>
        {onRegenerate && <RegenerateSection onRegenerate={onRegenerate} isRegenerating={isRegenerating} contentType="Infographic" />}
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-white">Infographic Design Brief</h2>
        <p className="text-white/60">{content.length} sections ready for design</p>
      </div>

      {/* Sections Grid */}
      <div className="grid md:grid-cols-2 gap-4">
        {content.map((section, index) => {
          // Header and footer span full width
          const isFullWidth = index === 0 || index === content.length - 1;
          
          return (
            <Card
              key={index}
              className={`card ${isFullWidth ? "md:col-span-2" : ""}`}
            >
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#00d4aa] flex items-center justify-center font-bold text-black text-lg">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-white mb-2">
                        {section.title}
                      </h3>
                      <p className="text-white/80 mb-3">{section.content}</p>
                      <div className="p-3 bg-white/5 rounded-lg border-l-4 border-[#00d4aa]">
                        <div className="text-xs font-mono text-white/40 uppercase mb-1">
                          Visual Direction
                        </div>
                        <p className="text-sm text-white/70 italic">{section.visual}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    {onRegenerateItem && (
                      <button
                        onClick={() => onRegenerateItem(index)}
                        disabled={regeneratingItem === index}
                        className="p-1 rounded hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                        title="Regenerate this section"
                      >
                        <RefreshCw size={13} className={regeneratingItem === index ? 'animate-spin' : ''} />
                      </button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => copyToClipboard(
                        `${section.title}\n\n${section.content}\n\nVisual: ${section.visual}`
                      )}
                      className="text-[#00d4aa] hover:bg-[#00d4aa]/10"
                    >
                      <Copy className="w-3 h-3" />
                    </Button>
                  </div>
                </div>

                {/* Generated Image */}
                {sectionImages[index] && (
                  <div className="rounded-lg overflow-hidden mt-4">
                    <img 
                      src={sectionImages[index]} 
                      alt={`Infographic Section ${index + 1}`}
                      className="w-full h-48 object-cover"
                    />
                  </div>
                )}

                {/* Generate Image Button */}
                <Button
                  onClick={() => generateImage(section, index)}
                  disabled={generatingImage === index}
                  variant="outline"
                  size="sm"
                  className="w-full border-purple-500/30 text-purple-400 hover:bg-purple-500/10"
                >
                  {generatingImage === index ? (
                    <>
                      <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      Generating...
                    </>
                  ) : (
                    <>
                      <ImageIcon className="w-3 h-3 mr-1" />
                      Generate Visual
                    </>
                  )}
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Copy All Button */}
      <Button
        onClick={copyAll}
        className="w-full bg-[#00d4aa] hover:bg-[#00e6bb] text-black font-semibold h-12"
      >
        <Copy className="w-4 h-4 mr-2" />
        Copy Full Design Brief
      </Button>

      {/* Design Notes */}
      <div className="p-6 bg-white/5 rounded-lg text-center">
        <p className="text-white/60 text-sm">
          These sections are designed to flow vertically. Share this brief with your designer
          or use tools like Canva, Figma, or Adobe Express to bring it to life.
        </p>
      </div>

      {/* Regenerate Section */}
      {onRegenerate && (
        <RegenerateSection
          onRegenerate={() => onRegenerate('infographic')}
          isRegenerating={isRegenerating}
          contentType="Infographic"
        />
      )}
    </div>
  );
}

export default InfographicTab;